/**
 * **The socket join-refusal vocabulary — `SOCKET-REFUSAL-COPY` (T-698), Dex
 * Add. 10 §1.**
 *
 * > ### **RESIDENCE: `packages/contracts`. Not `game-core`. Not the i18n catalogue.**
 *
 * Dex's three reasons, kept here because the next person to move this file
 * needs them and not a summary of them:
 *
 * 1. **The value's job is to cross a process boundary — that is the definition
 *    of a contract.** `game-core` holds shared *engine* code; a refusal token is
 *    not engine logic, it is **protocol**.
 * 2. **`contracts` is the only candidate all three services already watch**
 *    (`packages/contracts/**` is in api's, web's and game's `watchPatterns`;
 *    `game-core/**` is in web's and game's only). That matters because the HTTP
 *    refusal vocabulary lives in `apps/api`, and this is the only home that
 *    could ever hold both transports' tokens without inventing an
 *    `api → game-core` edge — see `LD-12` below.
 * 3. **i18n is REFUSED on a dependency fact and a design one.** `apps/game` does
 *    not watch `packages/i18n/**`, so the throw sites could not consume
 *    catalogue keys without a new edge — **and** it would conflate the protocol
 *    token with its presentation. *"Nora and Theo own the sentence; the token
 *    must be stable while the sentence changes."*
 *
 * **ONE DERIVATION, and the mirror-plus-fence option was refused.** The five
 * `BaseGameRoom` throw sites import these values; there is no second spelling
 * to fence. Juno #15.4 §5: *a definition lives where it is enforced and the
 * consumer imports it; a fence over two spellings is what you build only when an
 * import is impossible.* It was impossible across the TS/SQL boundary (`MX-10`);
 * across a TS package boundary it is trivially possible.
 *
 * ── LOCKED (Dex Add. 10 §1 guard 2) ────────────────────────────────────────
 *
 * **No refusal's code, token value or throw condition changes in the row that
 * introduced this file.** The values below are pinned byte-for-byte against the
 * five shipped literals in `room-refusal.pin.test.ts`, because the risk of the
 * re-point is *a typo silently changing a wire token* — so the row **proves**
 * the wire is unchanged rather than asserting it. `LD-7`'s widened predicate and
 * its socket re-check are untouched, and so is the HTTP path.
 *
 * ── `LD-12` `REFUSAL-TOKEN-TRANSPORT-PARITY` — named here, not fixed ───────
 *
 * The socket path throws `ALREADY_IN_LIVE_GAME`; the HTTP path returns
 * `already_in_live_game`. **The same refusal, two transports, two spellings,
 * and neither knows about the other.** Ledgered by Dex Add. 10 §2, truth value
 * FALSE today, trigger: *any row that ADDS or RENAMES a refusal on either
 * transport*. **`contracts` was chosen as the residence precisely so that row,
 * when it comes, has a home that already exists.**
 */
/**
 * The Colyseus close code every join refusal in this vocabulary carries.
 *
 * It is `4409` and it does not change: the client's discriminator is
 * `(code, reason)` and the code half is what tells it to look at the reason at
 * all. `ROOM_AUTH_ERROR` (`@wr/game-core`) is a **different** vocabulary — 4401
 * to 4405 and 4503, one refusal per code — and the two must not be merged: those
 * are facts about the *token*, these are facts about the *seat*.
 */
export const ROOM_JOIN_REFUSAL_CODE = 4409;
/**
 * **The closed set of socket join refusals.** Five today, and a sixth is a
 * deliberate, compiler-enforced event: `apps/web`'s composition map is typed
 * `Record<RoomJoinRefusal, …>`, so **adding a member here does not compile until
 * someone decides what the player sees** (Dex Add. 10 §1, A135's
 * registration-time forced question). That is the actual disease this row
 * treats — five refusals accumulated and nobody noticed the client could not
 * tell them apart.
 */
export const ROOM_JOIN_REFUSAL = {
    /** The socket arrived owning no `reserved`/`joined` participant row. */
    NO_SEAT_RESERVATION: "NO_SEAT_RESERVATION",
    /** This player holds a live seat at a *different* table (`LD-7`, Juno #15 §3). */
    ALREADY_IN_LIVE_GAME: "ALREADY_IN_LIVE_GAME",
    /** This player is already connected to *this* table on another socket. */
    ALREADY_SEATED: "ALREADY_SEATED",
    /** Every seat is taken. */
    ROOM_FULL: "ROOM_FULL",
    /** The player already left this room; re-entry is refused (one entry key per player per room). */
    SEAT_CLOSED: "SEAT_CLOSED",
};
/** Every member, in declaration order — the pin test's and the map's subject. */
export const ROOM_JOIN_REFUSALS = Object.values(ROOM_JOIN_REFUSAL);
/**
 * A runtime narrowing for the client, which receives the reason as an untyped
 * `message` string off the wire. **It answers "is this one of ours", never "is
 * this a refusal"** — an unknown reason is not this vocabulary's business and
 * falls through to the shipped `T6` behaviour unchanged.
 */
export function isRoomJoinRefusal(value) {
    return typeof value === "string" && ROOM_JOIN_REFUSALS.includes(value);
}
//# sourceMappingURL=room-refusal.js.map